import { useCallback, useEffect, useState } from 'react'
import { GitCommit, RefreshCw, ExternalLink, AlertCircle } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface Commit {
  sha: string
  full_sha: string
  author: string
  timestamp: string
  subject: string
  body: string
  url: string
  tag: string
}

const TAG_STYLE: Record<string, string> = {
  cyber:     'bg-rose/10    text-rose-light    border-rose/30',
  globe:     'bg-blue/10    text-blue-light    border-blue/30',
  alerting:  'bg-amber/10   text-amber-light   border-amber/30',
  connector: 'bg-emerald/10 text-emerald-light border-emerald/30',
  perf:      'bg-purple/10  text-purple-light  border-purple/30',
  ui:        'bg-blue/10    text-blue-light    border-blue/30',
  fix:       'bg-amber/10   text-amber-light   border-amber/30',
  deploy:    'bg-surface-hover text-text-secondary border-border',
  feature:   'bg-surface-hover text-text-secondary border-border',
}

function relative(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }) } catch { return ts }
}

export default function ChangelogPage() {
  const [data, setData] = useState<{ available: boolean; reason?: string; repo?: string; commits: Commit[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [tagFilter, setTagFilter] = useState<string>('all')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/changelog', { params: { limit: 100 } })
      setData(r.data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const commits = data?.commits || []
  const filtered = tagFilter === 'all' ? commits : commits.filter(c => c.tag === tagFilter)

  const tagCounts: Record<string, number> = {}
  for (const c of commits) tagCounts[c.tag] = (tagCounts[c.tag] || 0) + 1

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <GitCommit size={24} className="text-blue" /> Changelog
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            What shipped · {data?.repo ? (
              <a href={`https://github.com/${data.repo}/commits`} target="_blank" rel="noopener" className="text-blue-light hover:underline">
                {data.repo} <ExternalLink size={10} className="inline" />
              </a>
            ) : '—'}
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {data && !data.available && (
        <div className="card p-3 text-xs text-amber-light border-amber/30 bg-amber/5 flex items-start gap-2">
          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
          <div>{data.reason || 'Changelog unavailable'}. Set GITHUB_REPO + optionally GITHUB_TOKEN in backend/.env.</div>
        </div>
      )}

      {/* Tag filter bar */}
      {commits.length > 0 && (
        <div className="card p-2 flex items-center gap-1.5 flex-wrap text-[10px]">
          <button type="button" onClick={() => setTagFilter('all')}
            className={clsx('px-2 py-1 rounded border transition-colors',
              tagFilter === 'all' ? 'bg-blue/20 text-blue-light border-blue/30 font-semibold' : 'border-border text-text-muted hover:bg-surface-hover')}>
            all ({commits.length})
          </button>
          {Object.entries(tagCounts).sort(([, a], [, b]) => b - a).map(([t, n]) => (
            <button key={t} type="button" onClick={() => setTagFilter(t)}
              className={clsx('px-2 py-1 rounded border transition-colors',
                tagFilter === t
                  ? (TAG_STYLE[t] || '') + ' font-semibold'
                  : 'border-border text-text-muted hover:bg-surface-hover')}>
              {t} ({n})
            </button>
          ))}
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <div className="divide-y divide-border">
          {filtered.map(c => {
            const isOpen = expanded === c.sha
            const tagClass = TAG_STYLE[c.tag] || TAG_STYLE.feature
            return (
              <div key={c.sha} className="p-3 hover:bg-surface-hover transition-colors">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1 flex-shrink-0 w-16">
                    <span className={clsx('text-[9px] font-mono px-1.5 py-0.5 rounded border', tagClass)}>{c.tag}</span>
                    <a href={c.url} target="_blank" rel="noopener"
                       className="text-[10px] font-mono text-text-muted hover:text-blue-light">{c.sha}</a>
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => c.body && setExpanded(isOpen ? null : c.sha)}>
                    <div className="text-sm text-text-primary leading-snug">{c.subject}</div>
                    <div className="text-[10px] text-text-muted mt-1 flex items-center gap-2">
                      <span>{c.author}</span>
                      <span>·</span>
                      <span>{relative(c.timestamp)}</span>
                      {c.body && !isOpen && <span className="text-text-muted/70">· click for detail</span>}
                    </div>
                    {isOpen && c.body && (
                      <pre className="mt-2 p-2 rounded bg-surface-hover border border-border text-[11px] text-text-secondary whitespace-pre-wrap font-sans leading-relaxed">
                        {c.body}
                      </pre>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div className="p-6 text-center text-text-muted text-sm">
              {loading ? 'Loading commits…' : 'No commits match the filter.'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
